function supportsLocalStorage() {
	try {
		const testKey = "_test";
		window.localStorage.setItem(testKey, "1");
		window.localStorage.removeItem(testKey);
		return true;
	} catch (error: unknown) {
		return false;
	}
}

/**
 * In-memory storage used as fallback for localStorage.
 */
class MemoryStorage implements Storage {
	constructor(private _data: Record<string, string> = {}) {}

	public get length(): number {
		return Object.keys(this._data).length;
	}

	public key(index: number): string | null {
		return Object.values(this._data)[index] ?? null;
	}

	public getItem(key: string): string | null {
		return this._data[key] ?? null;
	}

	public setItem(key: string, value: string): void {
		this._data[key] = value;
	}

	public removeItem(key: string): void {
		delete this._data[key];
	}

	public clear(): void {
		this._data = {};
	}
}

export class DataElement {
	constructor(
		private readonly _storage: Storage,
		private readonly _id: string,
	) {}

	public get(): string | null {
		return this._storage.getItem(this._id);
	}

	public set(value: string): void {
		this._storage.setItem(this._id, value);
	}
}

export class DataStorage {
	private _storage: Storage;
	private _sessionData: DataElement;
	private _designIdentity: DataElement;

	constructor(useLocalStorage: boolean) {
		this._storage =
			useLocalStorage && supportsLocalStorage()
				? window.localStorage
				: new MemoryStorage();
		this._sessionData = new DataElement(this._storage, "sessionData");
		this._designIdentity = new DataElement(this._storage, "designIdentity");
	}

	get sessionData(): DataElement {
		return this._sessionData;
	}

	/**
	 * The saved design's ID and name. Kept apart from the session data, which
	 * is also the format of the save payload and of `?configuration=` links.
	 */
	get designIdentity(): DataElement {
		return this._designIdentity;
	}

	get isPersistent(): boolean {
		return !(this._storage instanceof MemoryStorage);
	}
}
